import clsx from 'clsx';
import { Formik } from 'formik';
import React, { useMemo, useRef } from 'react';
import { Form } from 'react-bootstrap';

import { PrimaryButton } from '@components/Button/variants';
import TextField from '@components/Form/TextField';
import { useEventTracking, useTeamSubscriptionCreate } from '@hooks';
import { useTeamUserCreate } from '@hooks';
import { ClickTarget, TypingInput, click, typing } from '@tracking';

import schema from './schema';
import styles from './styles.module.scss';

const EmailForm = ({
  companyName,
  currentUserEmail,
  guid,
}: {
  companyName: string;
  currentUserEmail: string;
  guid: string;
}) => {
  const { mutate: createTeamSubscription, isLoading: isCreateTeamSubscriptionLoading } =
    useTeamSubscriptionCreate();
  const {
    mutateAsync: createTeamUser,
    isError,
    isLoading: isCreatUserLoading,
  } = useTeamUserCreate();
  const { trackEvent } = useEventTracking();
  const leadInputStart = useRef<boolean>(false);

  const initialState = useMemo(
    () => ({
      email: currentUserEmail || '',
    }),
    [currentUserEmail]
  );

  const handleSubmit = async ({ email }) => {
    trackEvent(
      click({
        clickTarget: ClickTarget.TeamLeadCapture,
        clickText: 'Continue with email',
      })
    );
    if (!currentUserEmail) {
      await createTeamUser({ user: { email, skipVerifyTeamDomain: true } });
      if (!isError) {
        createTeamSubscription({ guid });
      }
    } else {
      createTeamSubscription({ guid });
    }
  };

  const handleInputChange = () => {
    if (!leadInputStart.current) {
      leadInputStart.current = true;
      trackEvent(typing({ inputTarget: TypingInput.TeamLeadCapture }));
    }
  };

  return (
    <div className="container">
      <div className={clsx(styles.formWrapper, 'row')}>
        <div className={clsx(styles.headerContainer, styles.companyName)}>{companyName}</div>

        <div className={styles.headerContainer}>
          <div className={styles.headerWrapper}>
            {currentUserEmail ? (
              <span>To activate your corporate subscription click button below to continue</span>
            ) : (
              <span>
                To activate your corporate subscription please enter your corporate email address
              </span>
            )}
          </div>
        </div>
        <Formik
          enableReinitialize
          initialValues={initialState}
          onSubmit={handleSubmit}
          validationSchema={schema}
        >
          {(formik) => (
            <Form noValidate onSubmit={formik.handleSubmit} className={styles.formContainer}>
              {currentUserEmail ? (
                <div className={styles.headerContainer}>{currentUserEmail}</div>
              ) : (
                <TextField
                  onChangeCapture={handleInputChange}
                  className="mb-24"
                  name="email"
                  placeholder="Enter your corporate email"
                />
              )}
              <div className={styles.buttonContainer}>
                <PrimaryButton
                  as="button"
                  type="submit"
                  disabled={!currentUserEmail && !(formik.isValid && formik.dirty)}
                  fullWidth
                >
                  {isCreateTeamSubscriptionLoading || isCreatUserLoading
                    ? 'Processing'
                    : 'Continue with email'}
                </PrimaryButton>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default EmailForm;
